import { AccountsContext } from '@paperstac/common/lib/components/AccountsProvider';
import { IdentityContext } from '@paperstac/common/lib/components/IdentityProvider';
import {
  ADD_NOTE_PHOTOS,
  DELETE_NOTE_PHOTO,
  REORDER_NOTE_PHOTOS,
} from '@paperstac/common/lib/serverDispatchActionTypes';
import { idIncrementor } from '@paperstac/common/lib/services/dateid';
import serverDispatch from '@paperstac/common/lib/services/serverDispatch';
import storage from '@paperstac/firebase/lib/storage';
import Box from '@paperstac/ui/lib/Box';
import Button from '@paperstac/ui/lib/Button';
import DescriptionGridCell from '@paperstac/ui/lib/DescriptionGridCell';
import DescriptionGridTerm from '@paperstac/ui/lib/DescriptionGridTerm';
import Flex from '@paperstac/ui/lib/Flex';
import FileInputButton from '@paperstac/ui/lib/form/FileInputButton';
import ExternalLinkIcon from '@paperstac/ui/lib/icons/ExternalLinkIcon';
import InfoCircleIcon from '@paperstac/ui/lib/icons/InfoCircleIcon';
import XIcon from '@paperstac/ui/lib/icons/XIcon';
import Image from '@paperstac/ui/lib/Image';
import Link from '@paperstac/ui/lib/Link';
import LinkButton from '@paperstac/ui/lib/LinkButton';
import Text from '@paperstac/ui/lib/Text';
import arrayMove from 'array-move';
import React from 'react';
import { ManageListingOverlayContext } from './ManageListingOverlayProvider';

const dateId = idIncrementor();

const getNotePhotos = (note) => (Array.isArray(note.photos) ? note.photos : []);

const NoteScreenPhotos = React.memo(() => {
  const { uid } = React.useContext(IdentityContext);
  const { currentAccountId } = React.useContext(AccountsContext);
  const { note } = React.useContext(ManageListingOverlayContext);
  const notePhotos = React.useMemo(() => getNotePhotos(note), [note]);
  const [photos, setPhotos] = React.useState(() => notePhotos);
  const [isUploading, setUploading] = React.useState(false);
  const [deletedIds, setDeletedIds] = React.useState([]);
  const [draggedPhotoId, setDraggedPhotoId] = React.useState(null);

  const handleAdd = React.useCallback(
    async (fileList) => {
      if (isUploading) return;
      setUploading(true);
      const files = [];
      for (let i = 0; i < fileList.length; i++) files.push(fileList[i]);
      const uploadedPhotos = await Promise.all(
        files.map((file) => {
          const id = dateId();
          const filePath = `user-uploads/${uid}/${currentAccountId}/notes/${note.id}/photos/${id}-${file.name}`;
          return storage
            .child(filePath)
            .put(file, { cacheControl: 'public,max-age=31536000' })
            .then(() => ({ id, filePath }));
        })
      );
      serverDispatch({
        action: ADD_NOTE_PHOTOS,
        payload: { accountId: note.accountId, noteId: note.id, photos: uploadedPhotos },
      })
        .catch((e) => alert(e.message))
        .finally(() => setUploading(false));
    },
    [currentAccountId, isUploading, note.accountId, note.id, uid]
  );

  const handleSort = React.useCallback(
    (oldIndex, newIndex) => {
      if (oldIndex === newIndex) return;
      const sortedPhotos = arrayMove(photos, oldIndex, newIndex).filter(({ id }) => !deletedIds.includes(id));
      setPhotos(sortedPhotos);
      serverDispatch({
        action: REORDER_NOTE_PHOTOS,
        payload: {
          accountId: note.accountId,
          noteId: note.id,
          order: sortedPhotos.map(({ id }) => id),
        },
      }).catch((e) => alert(e.message));
    },
    [deletedIds, note.accountId, note.id, photos]
  );

  const handleDelete = React.useCallback(
    (photoId) => {
      setDeletedIds([...deletedIds, photoId]);
      setPhotos(notePhotos.filter(({ id }) => ![...deletedIds, photoId].includes(id)));
      serverDispatch({
        action: DELETE_NOTE_PHOTO,
        payload: { accountId: note.accountId, noteId: note.id, photoId },
      }).catch((e) => alert(e.message));
    },
    [deletedIds, note.accountId, note.id, notePhotos]
  );

  const handleDragEnd = React.useCallback(() => {
    setDraggedPhotoId(null);
  }, []);

  const handleDragOver = React.useCallback((event) => {
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDragStart = React.useCallback((photoId, event) => {
    setDraggedPhotoId(photoId);
    if (!event.dataTransfer) return;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', photoId);
  }, []);

  const handleDrop = React.useCallback(
    (photoId, event) => {
      event.preventDefault();
      const droppedPhotoId = event.dataTransfer?.getData('text/plain') || draggedPhotoId;
      const oldIndex = photos.findIndex(({ id }) => id === droppedPhotoId);
      const newIndex = photos.findIndex(({ id }) => id === photoId);
      setDraggedPhotoId(null);
      if (oldIndex === -1 || newIndex === -1) return;
      handleSort(oldIndex, newIndex);
    },
    [draggedPhotoId, handleSort, photos]
  );

  React.useEffect(() => {
    setPhotos(notePhotos.filter(({ id }) => !deletedIds.includes(id)));
  }, [deletedIds, notePhotos]);

  return (
    <DescriptionGridCell>
      <DescriptionGridTerm mb={3}>
        Property Photos (
        {isUploading ? (
          <Box sx={{ textTransform: 'none', display: 'inline', color: 'primary' }}>Uploading...</Box>
        ) : (
          <FileInputButton
            accept="image/png,image/gif,image/jpeg"
            controlComponent={LinkButton}
            multiple={true}
            onFiles={handleAdd}
            sx={{ textTransform: 'none' }}
          >
            <Text variant="bold">+</Text> Add Photos
          </FileInputButton>
        )}
        )
      </DescriptionGridTerm>
      {!photos.length && (
        <Box fontSize={1}>
          <InfoCircleIcon sx={{ color: 'blue', position: 'relative', top: '-2px' }} /> <Text variant="bold">TIP</Text>:
          Notes with property photos sell quicker and for more money!
        </Box>
      )}
      <PhotoList
        onDelete={handleDelete}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDragStart={handleDragStart}
        onDrop={handleDrop}
        photos={photos}
      />
    </DescriptionGridCell>
  );
});

const PhotoImage = ({ fileUrl }) => (
  <Image
    height={60}
    src={fileUrl}
    sx={{
      bg: 'gray.9',
      cursor: 'move',
      display: 'block',
    }}
    width={90}
  />
);

const PhotoItem = ({ onDelete, onDragEnd, onDragOver, onDragStart, onDrop, photo }) => (
  <Box
    draggable={true}
    onDragEnd={onDragEnd}
    onDragOver={onDragOver}
    onDragStart={(event) => onDragStart(photo.id, event)}
    onDrop={(event) => onDrop(photo.id, event)}
    sx={{
      display: 'inline-block',
      mr: 2,
      mt: 2,
      zIndex: 9999,
    }}
  >
    <PhotoImage fileUrl={photo.fileUrl} />
    <Flex>
      <Button
        as={Link}
        flex="1 1 0"
        href={photo.fileUrl}
        size="small"
        sx={{ borderRadius: 0 }}
        target="_blank"
        variant="default"
      >
        <ExternalLinkIcon size={11} />
      </Button>
      <Button flex="1 1 0" onClick={() => onDelete(photo.id)} size="small" sx={{ borderRadius: 0 }} variant="red">
        <XIcon size={9} />
      </Button>
    </Flex>
  </Box>
);

const PhotoList = ({ onDelete, onDragEnd, onDragOver, onDragStart, onDrop, photos }) => (
  <div>
    {photos.map((photo) => (
      <PhotoItem
        key={photo.id}
        onDelete={onDelete}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        onDragStart={onDragStart}
        onDrop={onDrop}
        photo={photo}
      />
    ))}
  </div>
);

NoteScreenPhotos.propTypes = {};

NoteScreenPhotos.defaultProps = {};

NoteScreenPhotos.displayName = 'NoteScreenPhotos';

export default NoteScreenPhotos;
